/* prettier-ignore-file */
/**
 * Device Trust Management
 * Implements "Remember this device" functionality for PIN authentication
 */

const DEVICE_TRUST_COOKIE_NAME = "booking_trusted_device";
const DEVICE_TRUST_DURATION_DAYS = 60;

/**
 * Generate a unique device identifier
 */
function generateDeviceId() {
  const crypto = window.crypto || window.msCrypto;
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Set a cookie
 */
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Strict${secure}`;
}

/**
 * Get a cookie value
 */
function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Trust this device for a specific booking
 * @param {string} bookingId - The booking ID
 * @param {string} deviceId - Optional device ID (generated if not provided)
 * @returns {string} The device ID used
 */
export function trustDevice(bookingId, deviceId = null) {
  if (!deviceId) {
    deviceId = generateDeviceId();
  }

  try {
    // Get existing trusted bookings
    const existingCookie = getCookie(DEVICE_TRUST_COOKIE_NAME);
    let trustedBookings = {};

    if (existingCookie) {
      try {
        trustedBookings = JSON.parse(decodeURIComponent(existingCookie));
      } catch (e) {
        console.error("Error parsing trusted devices cookie:", e);
        trustedBookings = {};
      }
    }

    // Add this booking to trusted list
    trustedBookings[bookingId] = {
      deviceId: deviceId,
      trustedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + DEVICE_TRUST_DURATION_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    };

    // Save updated cookie
    const cookieValue = encodeURIComponent(JSON.stringify(trustedBookings));
    setCookie(DEVICE_TRUST_COOKIE_NAME, cookieValue, DEVICE_TRUST_DURATION_DAYS);

    console.log("✅ Device trusted for booking:", bookingId);
    return deviceId;
  } catch (error) {
    console.error("❌ Error trusting device:", error);
    return null;
  }
}

/**
 * Check if this device is trusted for a specific booking
 * @param {string} bookingId - The booking ID to check
 * @returns {object|null} Trust info if trusted, null if not
 */
export function isDeviceTrusted(bookingId) {
  try {
    const existingCookie = getCookie(DEVICE_TRUST_COOKIE_NAME);

    if (!existingCookie) {
      return null;
    }

    let trustedBookings = {};
    try {
      trustedBookings = JSON.parse(decodeURIComponent(existingCookie));
    } catch (e) {
      console.error("Error parsing trusted devices cookie:", e);
      return null;
    }

    const trustInfo = trustedBookings[bookingId];

    if (!trustInfo) {
      return null;
    }

    // Check if trust has expired
    const expiresAt = new Date(trustInfo.expiresAt);
    const now = new Date();

    if (now > expiresAt) {
      console.log("⚠️ Device trust expired for booking:", bookingId);
      // Remove expired entry
      delete trustedBookings[bookingId];
      const cookieValue = encodeURIComponent(JSON.stringify(trustedBookings));
      setCookie(DEVICE_TRUST_COOKIE_NAME, cookieValue, DEVICE_TRUST_DURATION_DAYS);
      return null;
    }

    console.log("✅ Device is trusted for booking:", bookingId);
    return trustInfo;
  } catch (error) {
    console.error("❌ Error checking device trust:", error);
    return null;
  }
}

/**
 * Revoke trust for a specific booking
 * @param {string} bookingId - The booking ID
 */
export function revokeDeviceTrust(bookingId) {
  try {
    const existingCookie = getCookie(DEVICE_TRUST_COOKIE_NAME);

    if (!existingCookie) {
      return;
    }

    let trustedBookings = {};
    try {
      trustedBookings = JSON.parse(decodeURIComponent(existingCookie));
    } catch (e) {
      console.error("Error parsing trusted devices cookie:", e);
      return;
    }

    // Remove this booking
    delete trustedBookings[bookingId];

    // Save updated cookie (or delete if empty)
    if (Object.keys(trustedBookings).length === 0) {
      deleteCookie(DEVICE_TRUST_COOKIE_NAME);
    } else {
      const cookieValue = encodeURIComponent(JSON.stringify(trustedBookings));
      setCookie(DEVICE_TRUST_COOKIE_NAME, cookieValue, DEVICE_TRUST_DURATION_DAYS);
    }

    console.log("✅ Device trust revoked for booking:", bookingId);
  } catch (error) {
    console.error("❌ Error revoking device trust:", error);
  }
}

/**
 * Revoke all device trusts (logout/clear)
 */
export function revokeAllDeviceTrusts() {
  deleteCookie(DEVICE_TRUST_COOKIE_NAME);
  console.log("✅ All device trusts revoked");
}

/**
 * Get all trusted bookings for this device
 * @returns {object} Object with bookingId keys
 */
export function getAllTrustedBookings() {
  try {
    const existingCookie = getCookie(DEVICE_TRUST_COOKIE_NAME);

    if (!existingCookie) {
      return {};
    }

    return JSON.parse(decodeURIComponent(existingCookie));
  } catch (error) {
    console.error("❌ Error getting trusted bookings:", error);
    return {};
  }
}

